import { getContrastText } from "@babackapp/ui/lib/colors";
import type { EmailsThemeDto } from "@babackapp/private-api-client/types";
import type { Section } from "../blocks-types";
import { getThemeRadius, ThemeRadiusComponent } from "../../hooks";
import type { SocialNetworksColor, SocialNetworksVariant } from "./types";

const DEFAULT_SURFACE = {
  background: "#f0f0f0",
  card: "#ffffff",
};
const DEFAULT_PRIMARY = "#000000";
const DEFAULT_SECONDARY = "#000000";

function safeContrast(color: string): "#fff" | "#000" {
  try {
    return getContrastText(color) === "#fff" ? "#fff" : "#000";
  } catch {
    return "#000";
  }
}

function surfaceColor(
  theme: EmailsThemeDto | null | undefined,
  section: Section,
): string {
  if (section === "inside") {
    return theme?.cardColor ?? DEFAULT_SURFACE.card;
  }
  return theme?.backgroundColor ?? DEFAULT_SURFACE.background;
}

export interface SocialBadgeStyle {
  /** Background color of the shaped badge. */
  backgroundColor: string;
  /** Monochrome glyph tint that stays legible on the badge background. */
  glyphColor: "black" | "white";
  /**
   * Tailwind radius class for the badge shape: a full circle, or the theme's
   * radius (shared with buttons) for the square variant — so `radius: "none"`
   * yields real square corners.
   */
  borderRadiusClass: string;
}

/**
 * Resolves the shaped-badge styling shared by the email renderer and the config
 * panel preview.
 *
 * All three colors render the same glyph-only icons on a CSS-shaped background
 * (so the circle/square variant always applies):
 * - `default`   → monochrome badge contrasting with the section surface,
 *                 reproducing the legacy baked black/white badges.
 * - `primary`   → theme primary color.
 * - `secondary` → theme secondary color.
 */
export function getSocialBadgeStyle(
  theme: EmailsThemeDto | null | undefined,
  section: Section,
  variant: SocialNetworksVariant,
  color: SocialNetworksColor,
): SocialBadgeStyle {
  let backgroundColor: string;

  if (color === "primary") {
    backgroundColor = theme?.primaryColor ?? DEFAULT_PRIMARY;
  } else if (color === "secondary") {
    backgroundColor = theme?.secondaryColor ?? DEFAULT_SECONDARY;
  } else {
    // A monochrome badge that contrasts with the surface it sits on: dark badge
    // on a light section, light badge on a dark one — matching the legacy look.
    backgroundColor =
      safeContrast(surfaceColor(theme, section)) === "#fff"
        ? "#ffffff"
        : "#000000";
  }

  return {
    backgroundColor,
    glyphColor: safeContrast(backgroundColor) === "#fff" ? "white" : "black",
    borderRadiusClass:
      variant === "square"
        ? getThemeRadius(theme, ThemeRadiusComponent.BUTTON)
        : "rounded-full",
  };
}
